import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, GraduationCap, Globe, Scale, Award, ArrowLeftRight } from "lucide-react";
import { SITE, waUrl } from "@/lib/site";

const stats = [
  { value: `+${SITE.yearsExperience}`, label: "Anos de experiência" },
  { value: "+30", label: "Anos no Brasil" },
  { value: "Nº 45", label: "Matrícula JUCEA-AM" },
  { value: "ES→PT", label: "Idiomas oficiais" },
];

const highlights = [
  { icon: Scale, text: "Credenciado pela Justiça Federal do Amazonas e JUCEA-AM" },
  { icon: Award, text: "Certidões de Excelência e reconhecimentos oficiais" },
  { icon: GraduationCap, text: "Docência em SESC, SENAC, UEA e CETAM" },
  { icon: Globe, text: "Validade nacional e internacional para documentos brasileiros" },
];

const About = () => {
  return (
    <section id="sobre" data-testid="about-section" className="py-24 md:py-32 bg-white">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
          className="text-center"
        >
          <p className="overline mb-5 inline-flex items-center">
            <span className="gold-rule mr-3" />Sobre o Profissional
          </p>
          <h2
            data-testid="about-title"
            className="font-serif-display text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight"
          >
            Experiência, <span className="italic">Credibilidade</span> e
            <br /> Segurança Jurídica.
          </h2>
          <p className="mt-6 text-[#475569] text-base lg:text-lg max-w-2xl mx-auto">
            Três décadas dedicadas à tradução, interpretação e ao ensino —
            traduzidas em fé pública para você.
          </p>
        </motion.div>

        {/* Biography */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.1 }}
          className="mt-14 max-w-3xl mx-auto space-y-6 text-[#475569] text-base lg:text-lg leading-relaxed"
          data-testid="about-bio"
        >
          <p>
            <span className="text-[#0A1128] font-medium">
              Jorge Mario Arango Guzmán
            </span>{" "}
            é Tradutor e Intérprete Juramentado Espanhol{" "}
            <ArrowLeftRight size={16} className="inline align-middle mx-1 text-[#C5A059]" strokeWidth={1.75} />{" "}
            Português, com mais de{" "}
            <span className="text-[#0A1128] font-medium">15 anos de experiência</span>{" "}
            comprovada, credenciado pela Justiça Federal do Amazonas e, atualmente,
            pela JUCEA-AM — possuindo Certidões de Excelência e outros reconhecimentos
            oficiais em razão de seu desempenho técnico e profissional.
          </p>
          <p>
            Nascido na Colômbia, nacionalizado e radicado no Brasil há mais de{" "}
            <span className="text-[#0A1128] font-medium">30 anos</span>, cursou estudos
            superiores e atuou como professor, coordenador e colaborador em instituições
            reconhecidas — entre elas{" "}
            <span className="text-[#0A1128] font-medium">SESC, SENAC, UEA e CETAM</span>,
            entre outras instituições renomadas (particulares) — desenvolvendo atividades
            nas áreas da educação, tradução e interpretação.
          </p>
          <p>
            Ao longo de sua trajetória, consolidou-se como{" "}
            <span className="text-[#0A1128] font-medium">referência técnica</span>{" "}
            em sua área de atuação, reconhecido pela excelência, precisão linguística,
            ética profissional e compromisso com a qualidade dos serviços prestados.
          </p>
          <p>
            Especialista em traduções juramentadas e serviços de interpretação com
            validade legal em todo o território nacional — bem como em países e
            instituições onde documentos brasileiros necessitem de tradução oficial e
            assinatura de Tradutor Público e Intérprete Comercial devidamente
            credenciado pelo Governo Brasileiro — atende clientes particulares,
            empresas, escritórios jurídicos e instituições públicas, oferecendo
            soluções linguísticas com{" "}
            <span className="text-[#0A1128] font-medium">
              segurança, confiabilidade e rigor técnico
            </span>.
          </p>
        </motion.div>

        {/* Highlights row */}
        <motion.ul
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.15 }}
          className="mt-14 grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto"
        >
          {highlights.map((h, i) => {
            const Icon = h.icon;
            return (
              <li
                key={i}
                data-testid={`about-highlight-${i}`}
                className="flex items-start gap-4 p-5 border border-[#E2E8F0] rounded-sm bg-[#FAFAFA]"
              >
                <Icon size={22} strokeWidth={1.5} className="text-[#C5A059] flex-shrink-0 mt-0.5" />
                <span className="text-sm text-[#0A1128] leading-snug">{h.text}</span>
              </li>
            );
          })}
        </motion.ul>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-[#E2E8F0] border border-[#E2E8F0]"
        >
          {stats.map((s, i) => (
            <div
              key={i}
              data-testid={`about-stat-${i}`}
              className="bg-white p-8 text-center flex flex-col items-center justify-center"
            >
              <span className="font-serif-display text-3xl md:text-4xl text-[#0A1128]">
                {s.value}
              </span>
              <span className="mt-2 text-[11px] tracking-[0.2em] uppercase text-[#475569]">
                {s.label}
              </span>
            </div>
          ))}
        </motion.div>

        {/* Conversion CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mt-14 flex flex-col items-center text-center gap-5"
        >
          <p
            className="font-serif-display italic text-2xl md:text-3xl text-[#0A1128]"
            style={{ fontStyle: "italic" }}
          >
            Jorge Mario Arango Guzmán
          </p>
          <p className="text-[10px] tracking-[0.2em] uppercase text-[#475569] -mt-3">
            Tradutor Público · {SITE.matricula}
          </p>
          <a
            href={waUrl("Olá Jorge, gostaria de falar diretamente com você sobre uma tradução juramentada.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="about-whatsapp-cta"
            className="mt-2 inline-flex items-center gap-3 bg-[#25D366] text-white px-7 py-4 rounded-sm hover:bg-[#1DA851] transition-all hover:-translate-y-0.5 shadow-[0_8px_24px_-12px_rgba(37,211,102,0.6)]"
          >
            <MessageCircle size={18} />
            <span className="font-medium tracking-wide">Falar diretamente com o tradutor</span>
          </a>
          <p className="text-xs text-[#475569]">Resposta em até 1 hora útil · WhatsApp {SITE.whatsappDisplay}</p>
        </motion.div>
      </div>
    </section>
  );
};

export default About;
